import * as Yup from "yup";

const REQUIRED_FIELD_MESSAGE = "O campo é obrigatório";
const NUMBER_ONLY_MESSAGE = "Somente número";
const UNIT_SCHEDULE_ERROR = "Informe horário de funcionamento da unidade";
const BREAK_PERIOD_ERROR = "Informe corretamente o horário de intervalo";
const PARKING_REQUIRED_ERROR =
  "Estacionamento foi ativado porém nenhum campo foi preenchido!";
const PARKING_DISTANCE_ERROR = "Campo distância é somente para números";

const UNIT_PERIODS_ERROR = "Preencha início e fim nos dias de atendimento.";
const REPORT_PERIODS_ERROR =
  "Preencha início e fim nos dias de atendimento resultado.";
const COLLECT_PERIODS_ERROR =
  "Preencha início e fim nos dias de coleta resultado.";

type UnitPeriod = {
  day_id?: number;
  start_id?: string | number;
  end_id?: string | number;
};

type BreakPeriod = {
  start_id?: string | number;
  end_id?: string | number;
  starts_at?: string;
  ends_at?: string;
};

type RegionValue = { id?: number } | number | null | undefined;

export type ParkingFormItem = {
  parking: { value: string };
  street: { value: string };
  cep: { value: string };
  number: { value: string };
  distance: { value: string };
};

export type UnitFormSchemaContext = {
  parkingsActive: boolean;
  parkings: ParkingFormItem[];
};

const isPeriodPartiallyFilled = (period: UnitPeriod) => {
  const hasStart = Boolean(period?.start_id);
  const hasEnd = Boolean(period?.end_id);
  return hasStart !== hasEnd;
};

const isBreakPeriodFilled = (breakPeriods: BreakPeriod[] = []) => {
  const breakPeriod = breakPeriods[0] || {};
  return Boolean(breakPeriod.start_id || breakPeriod.end_id);
};

const hasAnyCompleteUnitPeriod = (periods: UnitPeriod[] = []) =>
  periods.some(
    (period) => Boolean(period?.start_id) && Boolean(period?.end_id)
  );

const validateBreakPeriods = (periods: BreakPeriod[] = []) => {
  const breakPeriod = periods[0] || {};
  const hasStart = Boolean(breakPeriod.start_id);
  const hasEnd = Boolean(breakPeriod.end_id);

  if (hasStart !== hasEnd) return false;

  if (!isBreakPeriodFilled(periods)) return true;

  return Boolean(breakPeriod.start_id && breakPeriod.end_id);
};

const validatePeriods = (periods: UnitPeriod[] = []) => {
  if (!Array.isArray(periods) || periods.length === 0) return true;

  return !periods.some(isPeriodPartiallyFilled);
};

const isNumericValue = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return true;
  return !Number.isNaN(Number(value));
};

const isValidRegion = (value: RegionValue) => {
  if (!value) return false;

  if (typeof value === "object" && "id" in value) {
    return Boolean(value.id);
  }

  if (typeof value === "number") {
    return value > 0;
  }

  return false;
};

const isValidType = (value: unknown) => Boolean(value) && value !== "undefined";

const requiredString = () => Yup.string().required(REQUIRED_FIELD_MESSAGE);

const numericOptional = () =>
  Yup.mixed().test("is-numeric", NUMBER_ONLY_MESSAGE, (value) =>
    isNumericValue(value)
  );

const requiredNumeric = () =>
  Yup.mixed()
    .required(REQUIRED_FIELD_MESSAGE)
    .test("is-numeric", NUMBER_ONLY_MESSAGE, (value) => isNumericValue(value));

const periodsArray = (errorMessage: string) =>
  Yup.array()
    .default([])
    .test("complete-periods", errorMessage, (periods) =>
      validatePeriods(periods as UnitPeriod[])
    );

const validateActiveParkings = (parkings: ParkingFormItem[]) => {
  let isParkingValid = true;
  let isParkingDistanceValid = true;

  parkings.forEach((item) => {
    const parkingFields = [
      item.parking,
      item.street,
      item.cep,
      item.number,
      item.distance,
    ];

    parkingFields.forEach((field) => {
      if (field?.value === "") {
        isParkingValid = false;
      }
    });

    if (!isNumericValue(item.distance?.value)) {
      isParkingDistanceValid = false;
    }
  });

  return { isParkingValid, isParkingDistanceValid };
};

const parkingInfoItemShape = Yup.object().shape({
  parking: requiredString(),
  addresses: Yup.object().shape({
    city_id: Yup.string(),
    street: Yup.string(),
    cep: Yup.string(),
    number: Yup.string(),
    distance: Yup.string(),
  }),
});

const transportItemShape = Yup.object().shape({
  transport: requiredString(),
});

export const unitScalarFieldsShape = {
  name: requiredString(),
  calendar_id: requiredNumeric(),
  motion_code: requiredNumeric(),
  cashier: requiredString(),
  exam_schedule: Yup.string(),
  result_schedule: Yup.string(),
  addresses: Yup.object().shape({
    neighborhood: Yup.string(),
    street: requiredString(),
    number: requiredString(),
    complement: Yup.string(),
    cep: requiredString(),
    latitude: numericOptional(),
    longitude: numericOptional(),
  }),
  unmet_products: Yup.string(),
  facilities: Yup.string(),
  reference: Yup.string(),
  parking_desk: Yup.string(),
  region_id: Yup.string().required("Selecione ao menos uma região"),
  type: Yup.mixed()
    .required(REQUIRED_FIELD_MESSAGE)
    .test("valid-type", REQUIRED_FIELD_MESSAGE, (value) => isValidType(value)),
  professional_code: Yup.mixed(),
  slug: requiredString(),
};

export const unitBooleanFieldsShape = {
  parking: Yup.boolean(),
  accessibility: Yup.boolean(),
  wheelchair: Yup.boolean(),
  next_subway: Yup.boolean(),
  transport: Yup.boolean(),
  open_unit: Yup.boolean(),
  coming_soon: Yup.boolean(),
  toxic_exam: Yup.boolean(),
  collection_point: Yup.boolean(),
};

export const unitArrayFieldsShape = {
  channels: Yup.array(),
  category_ids: Yup.array()
    .min(1, "Selecione ao menos um serviço atendido")
    .required("Selecione ao menos um serviço atendido"),
  covenants: Yup.array(),
  images: Yup.array(),
  image_map: Yup.string().nullable(),
  parkings: Yup.array().of(parkingInfoItemShape),
  transports: Yup.array().of(transportItemShape),
  unit_periods: periodsArray(UNIT_PERIODS_ERROR),
  break_periods: Yup.array()
    .default([])
    .test("valid-break-periods", BREAK_PERIOD_ERROR, (periods) =>
      validateBreakPeriods(periods as BreakPeriod[])
    ),
  report_periods: periodsArray(REPORT_PERIODS_ERROR),
  collect_periods: periodsArray(COLLECT_PERIODS_ERROR),
};

export const baseUnitShape = {
  ...unitScalarFieldsShape,
  ...unitBooleanFieldsShape,
  ...unitArrayFieldsShape,
};

export const createUnitValidationSchema = () =>
  Yup.object()
    .shape(baseUnitShape)
    .test(
      "break-requires-unit-schedule",
      UNIT_SCHEDULE_ERROR,
      function validateBreakRequiresUnitSchedule(data) {
        const breakPeriods = data?.break_periods as BreakPeriod[] | undefined;
        const unitPeriods = data?.unit_periods as UnitPeriod[] | undefined;

        if (
          !isBreakPeriodFilled(breakPeriods) ||
          hasAnyCompleteUnitPeriod(unitPeriods)
        ) {
          return true;
        }

        return this.createError({
          path: "unit_periods",
          message: UNIT_SCHEDULE_ERROR,
        });
      }
    );

export const unitValidationSchema = createUnitValidationSchema();

export type UnitFormValues = Yup.InferType<
  ReturnType<typeof createUnitValidationSchema>
>;

export interface FormUnitProps {
  unitId: string | null;
  hasRapidTests?: boolean;
  blockView?: boolean;
}
